import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react'
import { BsBarChart, BsGlobe2 } from 'react-icons/bs';
import { CiCalendar, CiGlobe } from 'react-icons/ci';
import { FaCircle } from 'react-icons/fa6';
import { FiCheckCircle, FiMessageSquare, FiTrendingDown } from 'react-icons/fi';
import { PiWarningCircle } from 'react-icons/pi';
import { StatCard } from '../StatsCard';
import ChannelSavingsChart from '../Charts/ChannelSavingCharts';
import ChannelsBreakDown from './ChannelsBreakdown';
import OptimizationTrends from '../Charts/OptimizationTrends';
import {  } from '@chakra-ui/icons';
import ChartSkeleton from '../Charts/Skeleton';
import { formatNumber, formatPrice } from '../../../utils/DefaultFunction';
import API from '../../../helpers/axiosInstance';
import moment from 'moment';

const Savings = ({ date, refresh }) => {
    const [loading, setLoading] = useState(false);
    const [savingsData, setSavingsData] = useState(null);

    const getApiHeaders = () => ({
        "x-api-key": localStorage.getItem("api-key"),
        "x-access-token": localStorage.getItem("customer-portal"),
    });
    const dateformated = moment(date);
    const month = dateformated.format("MM"); // "09"
    const year = dateformated.format("YYYY");
    const fetchsavingsData = async () => {
        setLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/dashboard/customer/saving?month=${month}&year=${year}${refresh}`;
            const response = await API.get(url, { headers: getApiHeaders() });


            setSavingsData(response.data);
        } catch (error) {
            console.error("Error fetching analytics:", error);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchsavingsData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    if (loading) {
        return (
            <Flex justify="center" align="center" h="300px">
                <Spinner size="lg" />
            </Flex>
        );
    }
    const phaseStyles = [
        {
            bg: "#fff7ed",
            icon: <PiWarningCircle color="#f54a00" />,
            titleColor: "#9f2d00",
            subtitleColor: "#ca3500",
        },
        {
            bg: "#f0fdf4",
            icon: <FiCheckCircle color="#00a63e" />,
            titleColor: "#016630",
            subtitleColor: "#008236",
        },
        {
            bg: "var(--brand-tint)",
            icon: <CiGlobe color="#155dfc" />,
            titleColor: "#193cb8",
            subtitleColor: "var(--brand-primary-hover)",
        },
    ];


    const formatRs = (value) =>
        `${savingsData?.customer_currency} ${Number(value).toFixed(2).toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })}`;

    return (
        <>
            <Box className="box-style-card" my={4}>
                <Flex direction="column" mb={4}>
                    <Flex align="center" gap={2}>
                        <BsGlobe2 size={20} color="#155dfc" />
                        <Text fontSize="md" fontWeight="semibold" color="gray.700">International Messaging Platform - Cost Optimization
                        </Text>
                    </Flex>
                    <Text fontSize="sm" color="#64748b">Smart cost savings through optimal channel selection based on message type and destination country</Text>
                </Flex>
                <Flex w="100%" gap={6} >
                    {/* Left Box */}
                    <Box flex="1" >
                        {/* Customer Profile */}
                        <Text fontSize="md" fontWeight="semibold" >
                            Customer Profile
                        </Text>

                        <Flex direction="column" gap={2} mb={4}>
                            <Flex justify="space-between">
                                <Text fontSize="sm" color="gray.600">Base Country:</Text>
                                <Text fontSize="sm" fontWeight="medium">{savingsData?.customer_country}</Text>
                            </Flex>
                            <Flex justify="space-between">
                                <Text fontSize="sm" color="gray.600">Contract Currency:</Text>
                                <Text fontSize="sm" fontWeight="medium">{savingsData?.customer_currency}</Text>
                            </Flex>
                            <Flex justify="space-between">
                                <Text fontSize="sm" color="gray.600">International Messages:</Text>
                                <Box bg="var(--brand-primary)" color="white" px={3} py={1} borderRadius="lg" fontSize="xs">
                                    Enabled
                                </Box>
                            </Flex>
                        </Flex>

                        {/* Message Distribution */}
                        <Text fontSize="md" fontWeight="semibold" >
                            Message Distribution
                        </Text>

                        <Flex gap={4}>
                            {/* OTP & Transactions */}
                            <Box flex="1" bg="var(--brand-tint)" p={4} borderRadius="lg">
                                <Text fontSize="sm" fontWeight="500" color="#193cb8">
                                    OTP & Transactions
                                </Text>

                                <Text fontSize="xs" color="#155dfc">Domestic: {savingsData?.stat?.msg_distribution.opt_tran?.local}</Text>
                                <Text fontSize="xs" color="#155dfc">International: {savingsData?.stat?.msg_distribution.opt_tran?.int}</Text>
                            </Box>

                            {/* Marketing & Info */}
                            <Box flex="1" bg="#faf5ff" p={4} borderRadius="lg">
                                <Text fontSize="sm" fontWeight="500" color="#6e11b0">
                                    Marketing & Info
                                </Text>
                                <Text fontSize="xs" color="#9810fa">Domestic: {savingsData?.stat?.msg_distribution.marketing_info?.local}</Text>
                                <Text fontSize="xs" color="#9810fa">International: {savingsData?.stat?.msg_distribution.marketing_info?.int}</Text>
                            </Box>
                        </Flex>
                    </Box>

                    {/* Right Box (Journey Phase Breakdown) */}
                    <Box flex="1">
                        <Text fontSize="md" fontWeight="semibold" mb={3}>
                            Cost Structure Insights
                        </Text>


                        <Flex gap={2} direction="column">
                            {savingsData?.cost_structure_insights.map((item, index) => {
                                const style = phaseStyles[index];
                                return (
                                    <Box
                                        key={index}
                                        bg={style.bg}
                                        borderRadius="15px"
                                        p={3}
                                        minW="200px"
                                    >
                                        <Flex align="start">
                                            <Box me={2} mt={1}>{style.icon}</Box>
                                            <Box>
                                                <Text
                                                    fontSize="sm"
                                                    color={style.titleColor}
                                                    fontWeight="medium"
                                                >
                                                    {item.type}
                                                </Text>
                                                <Text fontSize="xs" color={style.subtitleColor}>
                                                    {item.message}
                                                </Text>
                                            </Box>
                                        </Flex>
                                    </Box>
                                );
                            })}
                        </Flex>
                    </Box>





                </Flex>


            </Box>



            <Flex gap={6} wrap="wrap" justifyContent="space-between">
                <StatCard
                    title="Monthly Savings"
                    value={`${formatPrice(savingsData?.stat?.monthly_saving, "en-US", savingsData?.customer_currency, true)}`} // e.g. "292.87"
                    subtitle={"vs. most expensive option"}
                    icon={<FiTrendingDown />}
                    iconColor="#00a63e"
                    changeColor={"#00a63e"}
                    originalValue={formatPrice(savingsData?.stat?.monthly_saving)}
                />
                <StatCard
                    title="Annual Projected"
                    value={`${formatPrice(savingsData?.stat?.annual_projected, "en-US", savingsData?.customer_currency, true)}`} // e.g. "3280.18"
                    subtitle={"Estimated yearly savings"}
                    // changeColor="#00a63e"
                    icon={<CiCalendar />}
                    iconColor="#64748b"
                    originalValue={formatPrice(savingsData?.stat?.annual_projected)}
                />
                <StatCard
                    title={`${savingsData?.customer_country} Efficiency`}
                    value={savingsData?.stat?.local_efficiency} // e.g. "17.60%"
                    subtitle={"Domestic cost reduction"}
                    changeColor="#00a63e"
                    icon={<FaCircle />}
                    iconColor="#00a63e"
                />
                <StatCard
                    title="International Efficiency"
                    value={savingsData?.stat?.international_efficiency} // e.g. "0%"
                    subtitle={"International cost reduction"}
                    changeColor="#155dfc"
                    icon={<FaCircle />}
                    iconColor="#155dfc"
                />
                <StatCard
                    title="Total Messages"
                    value={formatNumber(savingsData?.stat?.total_message, "en-US", true)} // e.g. "115"
                    subtitle={"All channels combined"}
                    // changeColor="#00a63e"
                    icon={<FiMessageSquare />}
                    iconColor="#64748b"
                    originalValue={formatNumber(savingsData?.stat?.total_message)}
                />
            </Flex>


            <Box className="box-style-card" my={4}>
                <Flex direction="column" mb={4}>
                    <Flex align="center" gap={2}>
                        <BsGlobe2 size={20} color="#155dfc" />
                        <Text fontSize="md" fontWeight="semibold" color="gray.700">Cost Efficiency Analysis by Destination
                        </Text>
                    </Flex>
                    <Text fontSize="sm" color="#64748b">Detailed cost optimization performance for domestic vs international messaging</Text>
                </Flex>

                <Box>
                    <Flex gap={6} wrap="wrap">
                        {/* Domestic */}
                        <Box flex="1" p={5} borderRadius="10px">
                            <Flex align="center" mb={3} gap={2}>
                                <Box w={3} h={3} borderRadius="full" bg="#10b981" />
                                <Text fontWeight="medium" color="#016630">
                                    {savingsData?.customer_country} Domestic Messages
                                </Text>
                            </Flex>

                            <Box bg="#f0fdf4" borderRadius="8px" p={3} mb={2}>
                                <Flex justifyContent={"space-between"} alignItems={"center"}>
                                    <Text fontSize="sm" color="#008236">Current Monthly Cost</Text>
                                    <Text fontWeight="500" color="#008236">
                                        {formatPrice(savingsData?.cost_efficiency?.domestic.current_monthly_cost, "en-US", savingsData?.customer_currency, true)}
                                    </Text>
                                </Flex>
                            </Box>

                            <Box bg="#f0fdf4" borderRadius="8px" p={3} mb={2}>
                                <Flex justifyContent={"space-between"} alignItems={"center"}>
                                    <Text fontSize="sm" color="#008236">Most Expensive Option</Text>
                                    <Text fontWeight="500" color="#008236">

                                        {formatPrice(savingsData?.cost_efficiency?.domestic.most_expensive_option, "en-US", savingsData?.customer_currency, true)}
                                    </Text>
                                </Flex>
                            </Box>

                            <Box bg="#dcfce7" borderRadius="8px" p={3} mb={2} border={"1px"} borderColor={"#b9f8cf"} >
                                <Flex justifyContent={"space-between"} alignItems={"center"}>
                                    <Text fontSize="sm" color={"#016630"} fontWeight={"600"}>Savings</Text>
                                    <Text fontWeight="bold" color="#016630">

                                        {savingsData?.cost_efficiency?.domestic.cost_efficiency}
                                    </Text>
                                </Flex>
                            </Box>

                            <Box bg="#f0fdf4" borderRadius="8px" p={3}>
                                <Flex justifyContent={"space-between"} alignItems={"center"}>
                                    <Text fontSize="sm" color="#008236">Monthly Savings</Text>
                                    <Text fontWeight="500" color="#008236">
                                        {formatPrice(savingsData?.cost_efficiency?.domestic.monthly_savings, "en-US", savingsData?.customer_currency, true)}
                                    </Text>
                                </Flex>
                            </Box>
                        </Box>

                        {/* International */}
                        <Box flex="1" p={5} borderRadius="10px">
                            <Flex align="center" mb={3} gap={2}>
                                <Box w={3} h={3} borderRadius="full" bg="#3b82f6" />
                                <Text fontWeight="500" color="#193cb8">
                                    International Messages
                                </Text>
                            </Flex>

                            <Box bg="var(--brand-tint)" borderRadius="8px" p={3} mb={2}>
                                <Flex justifyContent={"space-between"} alignItems={"center"}>
                                    <Text fontSize="sm" color={"var(--brand-primary-hover)"}>Current Monthly Cost</Text>
                                    <Text fontWeight="400" color="#193cb8">
                                        {formatPrice(savingsData?.cost_efficiency?.international.current_monthly_cost, "en-US", savingsData?.customer_currency, true)}
                                    </Text>
                                </Flex>
                            </Box>

                            <Box bg="var(--brand-tint)" borderRadius="8px" p={3} mb={2}>
                                <Flex justifyContent={"space-between"} alignItems={"center"}>
                                    <Text fontSize="sm" color={"var(--brand-primary-hover)"}>Most Expensive Option</Text>
                                    <Text fontWeight="400" color="#193cb8">
                                        {formatPrice(savingsData?.cost_efficiency?.international.most_expensive_option, "en-US", savingsData?.customer_currency, true)}
                                    </Text>
                                </Flex>
                            </Box>

                            <Box bg="var(--brand-tint-strong)" borderRadius="8px" p={3} mb={2} border={"1px"} borderColor={"var(--brand-tint-strong)"}>
                                <Flex justifyContent={"space-between"} alignItems={"center"}>
                                    <Text fontSize="sm" color={"#193cb8"} fontWeight={"600"}>Savings</Text>
                                    <Text fontWeight="bold" color="#193cb8">
                                        {savingsData?.cost_efficiency?.international.cost_efficiency}
                                    </Text>
                                </Flex>
                            </Box>

                            <Box bg="var(--brand-tint)" borderRadius="8px" p={3}>
                                <Flex justifyContent={"space-between"} alignItems={"center"}>
                                    <Text fontSize="sm" color={"var(--brand-primary-hover)"}>Monthly Savings</Text>
                                    <Text fontWeight="400" color="#193cb8">
                                        {formatPrice(savingsData?.cost_efficiency?.international.monthly_savings, "en-US", savingsData?.customer_currency, true)}
                                    </Text>
                                </Flex>
                            </Box>
                        </Box>
                    </Flex>

                    {/* Insights */}
                    <Box mt={4} bg="gray.50" p={5} borderRadius="10px">
                        <Text fontWeight="semibold" mb={3}>
                            Cost Optimization Insights
                        </Text>

                        <Flex justify="space-between" wrap="wrap" textAlign="center">
                            <Box flex="1">
                                <Text fontSize="xs" color={"#64748b"}>Total Cost Savings</Text>
                                <Text fontWeight="500" fontSize={"lg"} color="#00a63e">
                                    {formatPrice(savingsData?.cost_efficiency?.insights.total_cost_savings, "en-US", savingsData?.customer_currency, true)}
                                </Text>
                            </Box>

                            <Box flex="1">
                                <Text fontSize="xs" color={"#64748b"}>Domestic vs Int'l Ratio</Text>
                                <Text fontWeight="500" fontSize={"lg"}>
                                    {savingsData?.cost_efficiency?.insights.domestic_vs_international_ratio}
                                </Text>
                            </Box>

                            <Box flex="1">
                                <Text fontSize="xs" color={"#64748b"}>Overall Efficiency</Text>
                                <Text fontWeight="500" fontSize={"lg"} color="#155dfc">
                                    {savingsData?.cost_efficiency?.insights.overall_efficiency}
                                </Text>
                            </Box>
                        </Flex>
                    </Box>
                </Box>


                <Box className="box-style-card" my={4}>
                    <Flex direction="column" mb={4}>
                        <Flex align="center" gap={2}>
                            <BsBarChart size={20} color="#155dfc" />
                            <Text fontSize="md" fontWeight="semibold" color="gray.700">Intelligent Channel Optimization Savings
                            </Text>
                        </Flex>
                        <Text fontSize="sm" color="#64748b">Cost savings by channel: Pakistan domestic vs International destinations</Text>
                    </Flex>
                    {savingsData?.channel_savings_breakup?.length > 0 ? <ChannelSavingsChart data={savingsData?.channel_savings_breakup} /> : <ChartSkeleton title={"Cost optimization insights will appear here"} />}

                </Box>
                <Box className="box-style-card" my={4}>
                    <Flex direction="column" mb={4}>
                        <Flex align="center" gap={2}>
                            <BsBarChart size={20} color="#155dfc" />
                            <Text fontSize="md" fontWeight="semibold" color="gray.700">Intelligent Channel Optimization Savings
                            </Text>
                        </Flex>
                        <Text fontSize="sm" color="#64748b">Cost savings by channel: Pakistan domestic vs International destinations</Text>
                    </Flex>
                    {savingsData?.channel_savings_breakup?.length > 0 ? <ChannelsBreakDown customer_country={savingsData?.customer_country}
                        customer_currency={savingsData?.customer_currency}
                        data={savingsData?.channel_savings_breakup} /> : <ChartSkeleton title={"Intelligent Channel Optimization insights will appear here"} />}

                </Box>
                <Box className="box-style-card" my={4}>
                    <Flex direction="column" mb={4}>
                        <Flex align="center" gap={2}>
                            <BsBarChart size={20} color="#155dfc" />
                            <Text fontSize="md" fontWeight="semibold" color="gray.700">6-Month Optimization Trend
                            </Text>
                        </Flex>
                        <Text fontSize="sm" color="#64748b">Track your cost optimization progress over time</Text>
                    </Flex>
                    {Object.entries(savingsData?.last6MonthsTrend ?? {}).length > 0 ? (
                        <OptimizationTrends data={savingsData.last6MonthsTrend} />
                    ) : (
                        <ChartSkeleton title="6-Month Optimization Trend insights will appear here" />
                    )}


                </Box>
                {/* <Box className="box-style-card" my={4}>
                    <Flex direction="column" mb={4}>
                        <Flex align="center" gap={2}>
                            <BsBarChart size={20} color="#155dfc" />
                            <Text fontSize="md" fontWeight="semibold" color="gray.700">Strategic Optimization Recommendations
                            </Text>
                        </Flex>
                        <Text fontSize="sm" color="#64748b">Country-specific and message-type aware cost optimization strategies</Text>
                    </Flex>


                    <Flex gap={2} direction="column">
                        <Box

                            bg={"#fff7ed"}
                            border={"1px"}
                            borderColor={"#ffd7a8"}
                            borderRadius="8px"
                            p={3}
                            minW="200px"
                        >
                            <Flex align="start">
                                <Box me={2} ><WarningIcon color={"#9f2d00"} /></Box>
                                <Box>
                                    <Text
                                        fontSize="sm"
                                        color={"#9f2d00"}
                                        fontWeight="medium"
                                    >
                                        OTP & Transaction Optimization
                                    </Text>
                                    <Text fontSize="xs" color={"#ca3500"}>
                                        For urgent OTP messages, prefer WhatsApp over SMS to save Rs 1.70 per domestic message and Rs 3.80 per international message.
                                    </Text>
                                </Box>
                            </Flex>
                        </Box>
                        <Box

                            bg={"#f0fdf4"}
                            border={"1px"}
                            borderColor={"#b9f8cf"}
                            borderRadius="8px"
                            p={3}
                            minW="200px"
                        >
                            <Flex align="start" >
                                <Box me={2} pt={1}><FiCheckCircle  color={"#016630"} /></Box>
                                <Box>
                                    <Text
                                        fontSize="sm"
                                        color={"#016630"}
                                        fontWeight="medium"
                                    >
                                        Marketing Campaign Optimization
                                    </Text>
                                    <Text fontSize="xs" color={"#008236"}>
                                        For marketing messages, use SMS instead of WhatsApp to save Rs 0.50 per domestic message. Consider email for non-time-sensitive campaigns to maximize savings.
                                    </Text>
                                </Box>
                            </Flex>
                        </Box>
                        <Box

                            bg={"var(--brand-tint)"}
                            border={"1px"}
                            borderColor={"var(--brand-tint-strong)"}
                            borderRadius="8px"
                            p={3}
                            minW="200px"
                        >
                            <Flex align="start">
                                <Box me={2} pt={1}><CiGlobe  color={"#193cb8"} /></Box>
                                <Box>
                                    <Text
                                        fontSize="sm"
                                        color={"#193cb8"}
                                        fontWeight="medium"
                                    >
                                        International Messaging Strategy
                                    </Text>
                                    <Text fontSize="xs" color={"var(--brand-primary-hover)"}>
                                        International messages cost 3-4x more. Prioritize email and Inbox notifications for non-urgent international communications. Use push notifications for app users to minimize international SMS/WhatsApp costs.
                                    </Text>
                                </Box>
                            </Flex>
                        </Box>
                        <Box

                            bg={"#faf5ff"}
                            border={"1px"}
                            borderColor={"#e9d4ff"}
                            borderRadius="8px"
                            p={3}
                            minW="200px"
                        >
                            <Flex align="start">
                                <Box me={2} pt={1}><BsBarChart color={"#6e11b0"} /></Box>
                                <Box>
                                    <Text
                                        fontSize="sm"
                                        color={"#6e11b0"}
                                        fontWeight="medium"
                                    >
                                        Multi-Currency Optimization
                                    </Text>
                                    <Text fontSize="xs" color={"#8200db"}>
                                        Monitor USD/PKR exchange rate fluctuations (current: $285). WhatsApp international pricing in USD can impact your PKR costs significantly during currency volatility.
                                    </Text>
                                </Box>
                            </Flex>
                        </Box>
                    </Flex>
                </Box> */}
            </Box>
        </>
    )
}

export default Savings